import 'styles/global.css'

import GoogleTagManager from 'components/analytics/google-tag-manager'
import HubspotScript from 'components/analytics/hubspot'
import Sentry from 'components/analytics/sentry'
import AppContextModal from 'components/layout/app-context-modal'
import FlashNotice from 'components/layout/flash-notice'
import Notification from 'components/layout/notification'
import Layout from 'components/layout/layout'
import Paywall from 'components/layout/paywall/index'
import SWRConfig from 'components/utilities/swr-config'
import { AppConsumer, AppProvider } from 'context/app-context'
import { AssetProvider } from 'context/asset-context'
import { CurrentUserProvider } from 'context/current-user-context'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { FC, useMemo } from 'react'
import type NextPageWithLayout from 'types/next-page-with-layout'

type AppPropsWithLayout = {
  Component: NextPageWithLayout
  pageProps: any
}

const App: FC<AppPropsWithLayout> = ({ Component, pageProps }) => {
  const router = useRouter()
  const getLayout = useMemo(
    () =>
      Component.getLayout ||
      ((page) => <Layout error={pageProps?.error}>{page}</Layout>),
    [Component.getLayout, pageProps?.error],
  )

  const componentWithLayout = useMemo(
    () => getLayout(<Component {...pageProps} />, router),
    [Component, getLayout, pageProps],
  )

  return (
    <AppProvider>
      <AppConsumer>
        {({ darkMode, env } = {}) => (
          <CurrentUserProvider>
            <SWRConfig>
              <AssetProvider>
                <Head>
                  <link
                    rel="icon"
                    href={`${env.CDN_URL}/images/favicon/favicon.ico`}
                  />
                  <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href={`${env.CDN_URL}/images/favicon/apple-touch-icon.png`}
                  />
                  <link
                    rel="icon"
                    type="image/png"
                    sizes="32x32"
                    href={`${env.CDN_URL}/images/favicon/favicon-32x32.png`}
                  />
                  <link
                    rel="icon"
                    type="image/png"
                    sizes="16x16"
                    href={`${env.CDN_URL}/images/favicon/favicon-16x16.png`}
                  />
                  <link
                    rel="manifest"
                    href={`${env.ENV ? env.CDN_URL : ''}/site.webmanifest`}
                  />
                  <link rel="preconnect" href={env.CDN_URL} />
                </Head>
                <div className={darkMode ? 'dark' : ''}>
                  <FlashNotice />
                  <Notification />
                  <Paywall>{componentWithLayout}</Paywall>
                  <AppContextModal />
                </div>
                <GoogleTagManager />
                <HubspotScript />
                <Sentry />
              </AssetProvider>
            </SWRConfig>
          </CurrentUserProvider>
        )}
      </AppConsumer>
    </AppProvider>
  )
}

export default App
